import { optionPolicy, QUESTION_TYPES, type QuestionType } from '@cyberscore/contracts';
import { ArrowDown, ArrowUp, Plus, Trash2, Zap } from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { AnswerInput } from '../assessments/AnswerInput';
import { useDocumentTypeInfo, useQuestionInfo } from './api';
import { ChecksEditor } from './ChecksEditor';
import { ConditionEditor } from './ConditionEditor';
import { useItemDraft } from './draft';
import {
  freeValue,
  previewQuestion,
  questionIssues,
  withType,
  type OptionDraft,
  type QuestionDraft,
} from './editors';
import { EditorHead, fromList, IssueList, toList } from './EditorParts';
import { EffectsEditor, type EffectDraft } from './EffectsEditor';
import { move } from './journey';

const typeLabel = (t: string) => t.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const FIELD: Record<string, string> = {
  text: 'Question',
  help: 'Help',
  options: 'Options',
  settings: 'Settings',
  visibility: 'When it is asked',
  metadata: 'Scoring',
  effects: 'Effects',
  checks: 'Document checks',
};
const fieldLabel = (path: string) => {
  const [head, i] = path.split('.');
  if (head === 'options' && i !== undefined) return `Option ${Number(i) + 1}`;
  return FIELD[head ?? ''] ?? (path || 'Question');
};

/**
 * The question editor (task 43): wording, answer type, options and their effects, when it is asked and how
 * it counts towards the score, with a live preview of what the person answering sees.
 */
export function QuestionEditor({ id }: { id: string }) {
  const d = useItemDraft<QuestionDraft>(id, href.question);
  const questions = useQuestionInfo();
  const docTypes = useDocumentTypeInfo();
  const [sample, setSample] = useState<unknown>(undefined);

  if (d.item.isPending || !d.draft) return <div className="empty">Loading…</div>;
  if (d.item.isError) return <div className="empty">{d.item.error.message}</div>;

  const q = d.draft;
  const off = !d.owns;
  const issues = questionIssues(q);
  const preview = previewQuestion(q);
  const policy = optionPolicy(q.type);
  const options = q.options ?? [];
  const set = (patch: Partial<QuestionDraft>) => d.setDraft((x) => ({ ...x, ...patch }));
  const setOption = (i: number, o: OptionDraft) => set({ options: options.map((x, j) => (j === i ? o : x)) });
  const others = questions.filter((x) => x.key !== d.item.data!.key);

  return (
    <>
      <EditorHead d={d} section="Questions" sectionHref={href.questions()} title={q.text} blocked={issues.length > 0} />
      <div className="builder">
        <div className="stack">
          <section className="card pad" aria-labelledby="qe-wording">
            <h2 id="qe-wording" className="section-title">Wording</h2>
            <div className="field">
              <label htmlFor="qe-text">Question</label>
              <textarea id="qe-text" className="input" rows={2} disabled={off} value={q.text} onChange={(e) => set({ text: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="qe-help">Help (why we ask, in plain words)</label>
              <textarea id="qe-help" className="input" rows={2} disabled={off} value={q.help ?? ''} onChange={(e) => set({ help: e.target.value })} />
            </div>
            <div className="two">
              <div className="field">
                <label htmlFor="qe-type">Answer type</label>
                <select id="qe-type" className="input" disabled={off} value={q.type} onChange={(e) => { setSample(undefined); d.setDraft((x) => withType(x, e.target.value as QuestionType)); }}>
                  {QUESTION_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {typeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <label className="toggle-row mt-auto">
                <input type="checkbox" disabled={off} checked={q.mandatory ?? false} onChange={(e) => set({ mandatory: e.target.checked })} /> Required
              </label>
            </div>
          </section>

          {policy !== 'none' && (
            <section className="card pad" aria-labelledby="qe-options">
              <h2 id="qe-options" className="section-title">Options</h2>
              <p className="muted small">The value is what rules and reports use; changing it later breaks them, so set it once.</p>
              <ol className="option-rows">
                {options.map((o, i) => (
                  <OptionRow
                    key={i}
                    o={o}
                    n={i + 1}
                    disabled={off}
                    onChange={(x) => setOption(i, x)}
                    onUp={i > 0 ? () => set({ options: move(options, i, i - 1) }) : undefined}
                    onDown={i < options.length - 1 ? () => set({ options: move(options, i, i + 1) }) : undefined}
                    onRemove={() => set({ options: options.filter((_, j) => j !== i) })}
                  />
                ))}
              </ol>
              {!off && (
                <button type="button" className="btn btn-sm" onClick={() => { const label = `Option ${options.length + 1}`; set({ options: [...options, { value: freeValue(options, label), label }] }); }}>
                  <Plus size={14} /> Add an option
                </button>
              )}
            </section>
          )}

          <section className="card pad" aria-labelledby="qe-when">
            <h2 id="qe-when" className="section-title">When it is asked</h2>
            <ConditionEditor value={q.visibility} onChange={(visibility) => set({ visibility })} questions={others} mode="question" disabled={off} emptyLabel="Always asked" />
            <label className="toggle-row mt">
              <input type="checkbox" disabled={off} checked={q.for_forms ?? false} onChange={(e) => set({ for_forms: e.target.checked || undefined })} />
              Only when an insurer form the assessment prepares reads it
            </label>
            <div className="field mt">
              <label htmlFor="qe-deps">Only after these are answered</label>
              <input id="qe-deps" className="input mono" list="qe-keys" disabled={off} value={toList(q.depends_on)} onChange={(e) => set({ depends_on: fromList(e.target.value) })} placeholder="Q010, Q011" />
              <datalist id="qe-keys">
                {others.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.text}
                  </option>
                ))}
              </datalist>
            </div>
          </section>

          <section className="card pad" aria-labelledby="qe-score">
            <h2 id="qe-score" className="section-title">Scoring</h2>
            <div className="two">
              <div className="field">
                <label htmlFor="qe-domain">Risk domain</label>
                <input id="qe-domain" className="input mono" disabled={off} value={q.metadata?.risk_domain ?? ''} onChange={(e) => set({ metadata: { ...q.metadata, risk_domain: e.target.value || undefined } })} placeholder="BCK" />
              </div>
              <div className="field">
                <label htmlFor="qe-controls">Controls</label>
                <input id="qe-controls" className="input mono" disabled={off} value={toList(q.metadata?.controls)} onChange={(e) => set({ metadata: { ...q.metadata, controls: fromList(e.target.value) } })} placeholder="BCK-01, BCK-02" />
              </div>
            </div>
            <label className="toggle-row mt">
              <input type="checkbox" disabled={off} checked={q.metadata?.kill_switch ?? false} onChange={(e) => set({ metadata: { ...q.metadata, kill_switch: e.target.checked } })} /> Kill switch: a bad answer caps the score (always confirmed with a tap in chat)
            </label>
          </section>

          <section className="card pad" aria-labelledby="qe-checks">
            <h2 id="qe-checks" className="section-title">Document checks</h2>
            <p className="muted small">Once answered, the answer is checked against the business’s own documents. A backed answer counts for more in the score; one a document contradicts goes to the underwriter.</p>
            <ChecksEditor value={q.checks ?? []} onChange={(checks) => set({ checks })} types={docTypes} disabled={off} />
          </section>

          <section className="card pad" aria-labelledby="qe-effects">
            <h2 id="qe-effects" className="section-title">{policy === 'none' ? 'What an answer sets off' : 'What any answer sets off'}</h2>
            <p className="muted small">{policy === 'none' ? 'Run whenever the question is answered, e.g. a reminder before a renewal date.' : 'Effects of a particular choice belong on its option above.'}</p>
            <EffectsEditor value={(q.effects ?? []) as EffectDraft[]} onChange={(effects) => set({ effects })} disabled={off} />
          </section>

          <section className="card pad" aria-labelledby="qe-settings">
            <h2 id="qe-settings" className="section-title">Settings for this type</h2>
            <JsonField key={`${d.item.data!.revision}:${q.type}`} value={q.settings ?? {}} disabled={off} onChange={(settings) => set({ settings })} label="Settings (JSON)" hint={settingsHint(q.type)} />
          </section>
        </div>

        <aside className="stack">
          <IssueList issues={issues} label={fieldLabel} />
          <section className="card pad" aria-labelledby="qe-preview">
            <div id="qe-preview" className="eyebrow">What the person answering sees</div>
            {preview ? (
              <div className="preview">
                <h3 className="preview-q">
                  {preview.text} {!preview.mandatory && <span className="badge optional">Optional</span>}
                </h3>
                {preview.help && <p className="why">{preview.help}</p>}
                <AnswerInput id="qe-sample" q={preview} value={sample} onChange={setSample} onPick={setSample} errors={{}} />
                {sample !== undefined && <p className="muted small mt">Answer: <span className="mono">{JSON.stringify(sample)}</span></p>}
              </div>
            ) : (
              <p className="muted small">Fix the problems above to see the preview.</p>
            )}
          </section>
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

function settingsHint(type: QuestionType): string {
  const hints: Partial<Record<QuestionType, string>> = {
    numeric: '{"min": 0, "max": 1000, "integer": true, "unit": "staff"}',
    currency: '{"min": 0, "currency": "MYR"}',
    date: '{"not_in_future": true}',
    free_text: '{"max_length": 1000, "multiline": true}',
    rating: '{"scale": 5}',
    multiple_choice: '{"min_selected": 1}',
    boolean: '{"allow_not_sure": true}',
  };
  return hints[type] ? `For example ${hints[type]}` : 'Empty uses the defaults.';
}

function OptionRow({
  o,
  n,
  disabled,
  onChange,
  onUp,
  onDown,
  onRemove,
}: {
  o: OptionDraft;
  n: number;
  disabled: boolean;
  onChange: (o: OptionDraft) => void;
  onUp?: () => void;
  onDown?: () => void;
  onRemove: () => void;
}) {
  const effects = o.effects ?? [];
  const [open, setOpen] = useState(false);
  return (
    <li className="option-row">
      <div className="row gap">
        <input className="input grow" aria-label={`Option ${n} label`} disabled={disabled} value={o.label} onChange={(e) => onChange({ ...o, label: e.target.value })} />
        <input className="input mono option-value" aria-label={`Option ${n} value`} disabled={disabled} value={o.value} onChange={(e) => onChange({ ...o, value: e.target.value })} />
        <button type="button" className={`btn btn-sm btn-ghost${effects.length ? ' on' : ''}`} aria-expanded={open} aria-label={`Effects of option ${n}`} onClick={() => setOpen(!open)}>
          <Zap size={13} /> {effects.length || ''}
        </button>
        {!disabled && (
          <span className="row">
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move option ${n} up`} disabled={!onUp} onClick={onUp}>
              <ArrowUp size={13} />
            </button>
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move option ${n} down`} disabled={!onDown} onClick={onDown}>
              <ArrowDown size={13} />
            </button>
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remove option ${n}`} onClick={onRemove}>
              <Trash2 size={13} />
            </button>
          </span>
        )}
      </div>
      {open && (
        <div className="option-effects">
          <div className="muted small">When this option is chosen:</div>
          <EffectsEditor value={effects} disabled={disabled} onChange={(x) => onChange({ ...o, effects: x })} />
        </div>
      )}
    </li>
  ); // prettier-ignore
}

/** A JSON object edited as text; only valid objects reach the draft. */
export function JsonField({
  value,
  onChange,
  disabled,
  label,
  hint,
}: {
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  disabled?: boolean;
  label: string;
  hint?: string;
}) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="field">
      <label htmlFor="json-field">{label}</label>
      <textarea
        id="json-field"
        className="input mono"
        rows={4}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const v = JSON.parse(e.target.value || '{}');
            if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error();
            setError(null);
            onChange(v);
          } catch {
            setError('Must be a JSON object, e.g. {"scale": 5}');
          }
        }}
      />
      {error ? <span className="error small">{error}</span> : hint && <span className="hint">{hint}</span>}
    </div>
  );
}
